import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import { DARK_COLORS_RAW, vals } from '@/constants';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

interface Props {
  alt: string;
  src: string;
  width: number;
  height: number;
  percentage: number;
  spring?: boolean;
}

function FoldableImage({
  alt,
  src,
  width,
  height,
  percentage,
  spring = false,
}: Props) {
  const prefersReducedMotion = usePrefersReducedMotion();

  const wrapperSpring = useSpring({
    transform: `translateY(${percentage / 4}%)`,
    '--percentage': percentage + '%',
    '--rotation': percentage * 1.8 + 'deg',
    config: {
      tension: 130,
      friction: 30,
    },
    immediate: prefersReducedMotion || !spring,
  });

  const backgroundImage = `url(${src})`;

  return (
    <Wrapper style={wrapperSpring}>
      <TopHalf
        style={{
          '--width': width + 'px',
          '--height': height / 2 + 'px',
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{
            width,
            height,
          }}
        />
      </TopHalf>
      <BottomHalf
        style={{
          '--width': width + 'px',
          '--height': height / 2 + 'px',
          backgroundImage,
          backgroundPosition: '0 100%',
        }}
      >
        <Shadow />
        <Backside />
      </BottomHalf>

      {/*
        Because the entire card is translating down during the fold, I'm seeing a flicker in the crook of the fold. Repeating our trick a third time, I can apply the image to a 2px-tall element positioned in the crook of the fold.

        If you aren't translating the card during folding, you shouldn't need this fix.
      */}
      <FlickerFixer
        style={{
          top: height / 2 + 'px',
          opacity: percentage > 50 ? 0 : 1,
          backgroundImage,
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled(animated.div)`
  display: inline-block;
  perspective: 1250px;

  /* Don't block text selection on the subsequent paragraphs */
  pointer-events: none;
`;

const Half = styled.div`
  position: relative;
  z-index: 2;
  width: var(--width);
  height: var(--height);
  background-size: cover;
`;

const TopHalf = styled(Half)`
  border-radius: 10px 10px 0 0;
  overflow: hidden;
`;

const BottomHalf = styled(Half)`
  transform: rotateX(var(--rotation));
  transform-origin: top center;
  transform-style: preserve-3d;
  border-radius: 0 0 10px 10px;
`;

const Backside = styled.div`
  position: absolute;
  inset: -1px;
  background: hsl(0deg 0% 100% / 0.9);
  transform: rotateX(180deg) translateZ(2px);
  backface-visibility: hidden;
  border-radius: 10px 10px 0 0;

  /* In Dark Mode, the paper background uses the light-gray “adaptive white”. Match it here. */
  html[data-color-mode='dark'] & {
    background: hsl(
      ${vals(DARK_COLORS_RAW.colorAdaptiveWhite)} / 0.9
    );
  }
`;

const Shadow = styled.div`
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 0 0 10px 10px;
  background: #000;
  transform: translateZ(0.01px);
  opacity: calc(var(--percentage) * 1.5);
  backface-visibility: hidden;
`;

const FlickerFixer = styled.div`
  position: absolute;
  z-index: 1;
  left: 0;
  width: 100%;
  height: 2px;
  background-position: 0% 50%;
`;

export default FoldableImage;
